/* === useCalqueEchap — inscrire un calque dans la pile d'Échap === */

import { useEffect, useId, useLayoutEffect, useRef } from 'react';

import { empilerCalque, retirerCalque } from './calqueStack';
import type { GestionnaireCalque } from './calqueStack';

export interface OptionsCalqueEchap {
  /** Faux : le calque reste dans la pile mais absorbe Échap sans se fermer. Défaut : vrai. */
  actif?: boolean;
  /** Remonte au sommet quand le focus entre dans `conteneur` (panneau de l'assistant). Défaut : faux. */
  persistant?: boolean;
  /** Élément du calque : requis pour qu'un calque persistant remonte au focus. */
  conteneur?: React.RefObject<HTMLElement | null>;
}

/**
 * Inscrit le calque dans la pile d'Échap (`calqueStack.ts`) tant que `ouvert`
 * est vrai : il prend le sommet à l'ouverture et le rend à la fermeture ou au
 * démontage, dans quelque ordre que ce soit. `onEscape` et `actif` sont lus
 * par ref à chaque frappe : les changer ne déplace pas le calque dans la pile.
 * Le composant n'écoute plus Échap lui-même.
 */
export function useCalqueEchap(
  ouvert: boolean,
  onEscape: () => void,
  options?: OptionsCalqueEchap,
): void {
  const id = useId();
  const actif = options?.actif ?? true;
  const persistant = options?.persistant ?? false;
  const conteneur = options?.conteneur;

  const gestionnaire = useRef<GestionnaireCalque>({ onEscape, actif });
  const refConteneur = useRef(conteneur);
  useLayoutEffect(() => {
    gestionnaire.current = { onEscape, actif };
    refConteneur.current = conteneur;
  });

  useEffect(() => {
    if (!ouvert) return undefined;
    empilerCalque(id, () => gestionnaire.current, {
      persistant,
      conteneur: () => refConteneur.current?.current ?? null,
    });
    return () => retirerCalque(id);
  }, [id, ouvert, persistant]);
}
